"use client";

import type { CSSProperties, ReactNode } from "react";
import { Button, type ButtonProps } from "@/components/ui/Button";
import { safeCapture } from "@/lib/analytics/safeCapture";

/**
 * Where a "Start Quiz" CTA lives — captured on `quiz_cta_clicked` so the
 * landing→/quiz step of the funnel is measurable. `useQuizWizardController`'s
 * `init()` only fires `quiz_started` once the backend has actually created or
 * resumed a questionnaire, so without this event a failed/slow init (or
 * someone who clicks but never lands on /quiz at all) is invisible.
 */
export type StartQuizCtaSource =
  | "hero"
  | "cta_band"
  | "nav"
  | "results_retake"
  | "shared_results"
  | "my_results"
  | "blog_post"
  | "region_page";

// Mirrors CampfireChatButton's prop shape (components/campfire/CampfireChatButton.tsx):
// pull individual fields off ButtonProps via indexed access rather than extending the
// href/button discriminated union directly.
type StartQuizButtonProps = {
  source: StartQuizCtaSource;
  /** Extra properties merged into `quiz_cta_clicked` (e.g. `post_slug` on blog posts). */
  properties?: Record<string, string | number | boolean | null>;
  variant?: ButtonProps["variant"];
  size?: ButtonProps["size"];
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
};

/**
 * `Button` with `href="/quiz"` hardcoded, plus a `quiz_cta_clicked` capture
 * on click. Exists so server components (Hero, CtaBand, results pages, ...)
 * can render an instrumented Start Quiz link without becoming client
 * components themselves — same pattern as `CampfireChatButton`.
 */
export function StartQuizButton({ source, properties, children, ...rest }: StartQuizButtonProps) {
  return (
    <Button
      href="/quiz"
      onClick={() => safeCapture("quiz_cta_clicked", { ...properties, source })}
      {...rest}
    >
      {children}
    </Button>
  );
}
